import React, { useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { CrmContactFilter } from '../contactList';
import { FacetOption, ListOption, TagOption } from '../filters/types';
import { TargetChoice, TargetPicker } from '../../tagging/TargetPicker';
import {
  ACTION_LABELS,
  BulkAction,
  ChangeOutcome,
  confirmLabel,
  describeChangeError,
  describeOutcome,
  describePlan,
  isAdding,
  isTagAction,
  MAX_TAGS_PER_CHANGE,
  people,
  selectionChangedTo,
  Target,
} from '../../tagging/tagging';
import {
  CRM_BULK_ADD_TAGS,
  CRM_BULK_ADD_TO_LIST,
  CRM_BULK_REMOVE_FROM_LIST,
  CRM_BULK_REMOVE_TAGS,
  CRM_CREATE_LIST,
  CRM_CREATE_TAG,
  CRM_LIST_CHOICES,
  CRM_SELECTION_COUNT,
  CRM_SELECTION_LISTS,
  CRM_SELECTION_TAGS,
  CRM_TAG_CHOICES,
} from '../../tagging/tagging.queries';
import { useDebounced } from '../../tagging/useDebounced';
import { Selection, selectionFilter } from './selection';

interface BulkChangeDialogProps {
  action: BulkAction;
  /** The ticked rows, or everyone the filter matches. Never `none`. */
  selection: Selection;
  /** The list's filter, as it sends it. */
  filter: CrmContactFilter | undefined;
  /** No search and no filter: "everyone" is the whole CRM. */
  everyone: boolean;
  /** `changed`: something was written (a change, or a new tag or list), so the list's data is stale. */
  onClose: (changed: boolean) => void;
}

interface TagChangeData {
  matched: number;
  tags: Array<{ changed: number; tag: { id: number; name: string } }>;
}

interface ListChangeData {
  matched: number;
  changed: number;
  list: { id: number; name: string };
}

/**
 * Adds a tag or list to a selection of the contact list, or takes it away
 * (card 19). Two steps, as CRM_CONTACT_QUERY_FRONTEND.md §17.3 says:
 *
 * 1. **The exact count.** Ticked rows are their own count; "everyone matching"
 *    is counted afresh (`crmContactCounts`), never the list's estimate. Beside
 *    each tag or list: how many of the selection already have it.
 * 2. **The change, with that number.** The API counts again in the same
 *    statement and changes nothing on any other number. That answer
 *    (CRM_SELECTION_CHANGED) carries the new number, which becomes the one to
 *    confirm.
 *
 * A new tag or list is made only when the change is confirmed, so cancelling
 * leaves nothing behind.
 */
export const BulkChangeDialog = ({ action, selection, filter, everyone, onClose }: BulkChangeDialogProps) => {
  const client = useApolloClient();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const tagging = isTagAction(action);
  const sent = useMemo(() => selectionFilter(selection, filter), [selection, filter]);
  const ticked = selection.kind === 'ids' ? selection.ids.length : undefined;

  const countQuery = useQuery<{ crmContactCounts: { total: number } }>(CRM_SELECTION_COUNT, {
    variables: { filter: sent },
    fetchPolicy: 'network-only',
    skip: ticked !== undefined,
  });
  const facetQuery = useQuery<{ crmContactFacet: Array<FacetOption> }>(tagging ? CRM_SELECTION_TAGS : CRM_SELECTION_LISTS, {
    variables: { filter: sent },
    fetchPolicy: 'network-only',
  });
  const tagsQuery = useQuery<{ crmTags: Array<TagOption> }>(CRM_TAG_CHOICES, { skip: !tagging });

  const [listText, setListText] = useState('');
  const listSearch = useDebounced(listText.trim());
  const listsQuery = useQuery<{ crmSegments: { total: number; items: Array<ListOption> } }>(CRM_LIST_CHOICES, {
    variables: listSearch ? { search: listSearch } : {},
    skip: action !== 'addList',
  });

  const [targets, setTargets] = useState<Array<Target>>([]);
  const [recount, setRecount] = useState<number>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>();
  const [outcome, setOutcome] = useState<ChangeOutcome>();
  const [madeSomething, setMadeSomething] = useState(false);

  const count = recount ?? ticked ?? countQuery.data?.crmContactCounts.total;

  const facet = facetQuery.data?.crmContactFacet;
  const have = useMemo(() => new Map((facet ?? []).map((value) => [Number(value.value), value.count])), [facet]);
  const tagById = useMemo(() => new Map((tagsQuery.data?.crmTags ?? []).map((tag) => [tag.id, tag])), [tagsQuery.data]);

  const choices: Array<TargetChoice> = useMemo(() => {
    const counted = (id: number) => (facet ? (have.get(id) ?? 0) : undefined);
    switch (action) {
      case 'addTag':
        // System tags are set by automation; the API refuses them.
        return (tagsQuery.data?.crmTags ?? [])
          .filter((tag) => !tag.isSystem)
          .map((tag) => ({ id: tag.id, name: tag.name, color: tag.color, count: counted(tag.id) }));
      case 'removeTag':
        return (facet ?? [])
          .map((value) => ({ id: Number(value.value), name: value.label ?? value.value ?? '', count: value.count }))
          .filter((choice) => tagById.has(choice.id) && !tagById.get(choice.id)?.isSystem)
          .map((choice) => ({ ...choice, color: tagById.get(choice.id)?.color }));
      case 'addList':
        return (listsQuery.data?.crmSegments.items ?? []).map((list) => ({
          id: list.id,
          name: list.name,
          count: counted(list.id),
        }));
      case 'removeList':
        return (facet ?? []).map((value) => ({
          id: Number(value.value),
          name: value.label ?? value.value ?? '',
          count: value.count,
        }));
    }
  }, [action, facet, have, tagsQuery.data, tagById, listsQuery.data]);

  const choicesLoading = facetQuery.loading || (tagging ? tagsQuery.loading : action === 'addList' && listsQuery.loading);
  const choicesError = facetQuery.error ?? tagsQuery.error ?? listsQuery.error;
  // More lists than were loaded: names are looked up as they are typed.
  const searchLists = action === 'addList' && (listsQuery.data?.crmSegments.total ?? 0) > 200;

  const plan = count !== undefined ? describePlan(action, targets, count) : '';
  const canRun = !running && !outcome && targets.length > 0 && count !== undefined && count > 0;

  const run = async () => {
    if (count === undefined) return;
    setRunning(true);
    setError(undefined);
    try {
      // Make what is new first; a retry then reuses it.
      const made: Array<Target> = [];
      for (const target of targets) {
        if (target.id !== undefined) {
          made.push(target);
          continue;
        }
        if (tagging) {
          const { data } = await client.mutate<{ crmCreateTag: TagOption }>({
            mutation: CRM_CREATE_TAG,
            variables: { name: target.name },
          });
          made.push({ id: data!.crmCreateTag.id, name: data!.crmCreateTag.name, have: 0 });
        } else {
          const { data } = await client.mutate<{ crmCreateList: ListOption }>({
            mutation: CRM_CREATE_LIST,
            variables: { name: target.name },
          });
          made.push({ id: data!.crmCreateList.id, name: data!.crmCreateList.name, have: 0 });
        }
        setMadeSomething(true);
      }
      setTargets(made);

      const ids = made.map((target) => target.id as number);
      if (tagging) {
        const field = action === 'addTag' ? 'crmBulkAddTags' : 'crmBulkRemoveTags';
        const { data } = await client.mutate<Record<string, TagChangeData>>({
          mutation: action === 'addTag' ? CRM_BULK_ADD_TAGS : CRM_BULK_REMOVE_TAGS,
          variables: { filter: sent, tagIds: ids, expectedCount: count },
        });
        const result = data![field];
        setOutcome({
          matched: result.matched,
          changes: result.tags.map((change) => ({ name: change.tag.name, changed: change.changed })),
        });
      } else {
        const field = action === 'addList' ? 'crmBulkAddToList' : 'crmBulkRemoveFromList';
        const { data } = await client.mutate<Record<string, ListChangeData>>({
          mutation: action === 'addList' ? CRM_BULK_ADD_TO_LIST : CRM_BULK_REMOVE_FROM_LIST,
          variables: { listId: ids[0], filter: sent, expectedCount: count },
        });
        const result = data![field];
        setOutcome({ matched: result.matched, changes: [{ name: result.list.name, changed: result.changed }] });
      }
    } catch (err) {
      setError(err);
      const now = selectionChangedTo(err);
      if (now !== undefined) setRecount(now);
    } finally {
      setRunning(false);
    }
  };

  const close = () => {
    if (running) return;
    onClose(Boolean(outcome) || madeSomething);
  };

  const who =
    ticked !== undefined
      ? `The ${people(ticked)} you ticked.`
      : count === undefined
        ? countQuery.error
          ? 'Could not count everyone the filter matches. Close this and try again.'
          : 'Counting everyone the search and filters match…'
        : `Everyone the search and filters match: ${people(count)}.`;

  return (
    <Dialog open onClose={close} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-bulk-title">
      <DialogTitle id="crm-bulk-title">{ACTION_LABELS[action]}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" component="p" sx={{ lineHeight: 1.6 }}>
            Who
          </Typography>
          <Typography variant="body2" aria-live="polite">
            {who}
          </Typography>
          {selection.kind === 'all' && everyone && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0 }}>
              No search or filter is set: this is everyone in the CRM.
            </Alert>
          )}
        </Box>

        {outcome ? (
          <Alert severity="success" role="status">
            {describeOutcome(action, outcome).map((line) => (
              <div key={line}>{line}</div>
            ))}
          </Alert>
        ) : (
          <Box>
            <Typography variant="overline" color="text.secondary" component="p" sx={{ lineHeight: 1.6 }}>
              {tagging
                ? action === 'addTag'
                  ? 'Tags to add'
                  : 'Tags to remove'
                : action === 'addList'
                  ? 'List'
                  : 'List to take them off'}
            </Typography>
            {choicesError ? (
              <Alert severity="error" variant="outlined">
                {describeChangeError(choicesError)}
              </Alert>
            ) : (
              <Box sx={{ pointerEvents: running ? 'none' : undefined, opacity: running ? 0.6 : 1 }}>
                <TargetPicker
                  noun={tagging ? 'tag' : 'list'}
                  choices={choices}
                  selected={targets}
                  onChange={setTargets}
                  max={tagging ? MAX_TAGS_PER_CHANGE : 1}
                  allowCreate={isAdding(action)}
                  onQueryChange={searchLists ? setListText : undefined}
                  loading={choicesLoading}
                  emptyText={
                    choicesLoading
                      ? 'Loading…'
                      : action === 'removeTag'
                        ? 'None of them has a tag that can be removed by hand.'
                        : action === 'removeList'
                          ? 'None of them is on a list.'
                          : undefined
                  }
                  footer={
                    facet && (
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                        {isAdding(action)
                          ? `The numbers are how many of these already ${tagging ? 'have each tag' : 'are on each list'}.`
                          : `The numbers are how many of these ${tagging ? 'have each tag' : 'are on each list'}.`}
                      </Typography>
                    )
                  }
                />
              </Box>
            )}
          </Box>
        )}

        {!outcome && plan && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {plan}
          </Typography>
        )}

        {error !== undefined && (
          <Alert severity={selectionChangedTo(error) !== undefined ? 'warning' : 'error'}>{describeChangeError(error)}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {outcome ? (
          <Button variant="contained" onClick={close} autoFocus>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={running}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={isAdding(action) ? 'primary' : 'error'}
              onClick={run}
              disabled={!canRun}
              startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
            >
              {count !== undefined ? confirmLabel(action, count) : ACTION_LABELS[action]}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
